import { ExpenseCategory } from '../expense-category'
import * as payment from '../payment'

/**
 * The sponsor-facing payment justification sentence.
 *
 * This reproduces the client's approved Excel formula on the
 * `New Justifications` tab, which is the contract with the sponsor:
 *
 * ```excel
 * IF(V2<>0,
 *   "We are requesting a payment of " & TEXT(V2,"$#,###.00") &
 *   " for the period of 7/24/26-8/18/26. The requested funds will be allocated as follows: " &
 *   TEXTJOIN("; ", TRUE, IF(B2:U2<>0, B$1:U$1 & ": $" & TEXT(B2:U2,"#,##0.00"), "")) & ". ")
 * ```
 *
 * Three details of that formula are load-bearing and easy to lose: only
 * non-zero categories appear, they appear in worksheet column order, and the
 * amount requested is the row total rather than the amount being drawn.
 */
export namespace Text {
  /**
   * The maximum length PMS accepts in the
   * `Payment Justification (Maximum 1000 Characters)` column.
   */
  export const MAX_LENGTH = 1000

  /**
   * Builds the justification for one document, or `undefined` when there is
   * nothing to request.
   *
   * A zero total produces no text, matching the formula's `IF(V2<>0, ...)`
   * guard: a document with no net expense has nothing to defend.
   *
   * Text longer than {@link MAX_LENGTH} is returned in full rather than cut.
   * Dropping allocation lines would leave the listed categories not adding up
   * to the amount requested, which is the one thing the sentence exists to
   * show; the caller flags the row for a reviewer to shorten instead.
   */
  export function from(params: {
    /** Amounts by sponsor-facing category label. */
    readonly amountsByCategory: Readonly<Record<string, number>>
    readonly totalCosts: number
    readonly period: Readonly<payment.Period>
  }): string | undefined {
    if (roundCurrency(params.totalCosts) === 0) {
      return undefined
    }

    const allocations = Object.entries(params.amountsByCategory)
      .map(([label, amount]) => [label, roundCurrency(amount)] as const)
      .filter(([, amount]) => amount !== 0)
      .sort(
        ([left], [right]) =>
          ExpenseCategory.displayOrderIndex(left) -
            ExpenseCategory.displayOrderIndex(right) ||
          left.localeCompare(right),
      )
      .map(([label, amount]) => `${label}: ${formatCategory(amount)}`)

    return [
      `We are requesting a payment of ${formatTotal(params.totalCosts)}`,
      ` for the period of ${payment.Period.format(params.period)}.`,
      ` The requested funds will be allocated as follows: ${allocations.join(
        '; ',
      )}.`,
    ].join('')
  }

  /**
   * Formats a category amount as the formula's `": $" & TEXT(x,"#,##0.00")`
   * does, so a negative amount reads `$-1,234.56` exactly as the client's
   * workbook renders it.
   */
  export function formatCategory(amount: number): string {
    return `$${_DECIMAL.format(roundCurrency(amount))}`
  }

  /**
   * Formats the requested total as the formula's `TEXT(x,"$#,###.00")` does.
   *
   * `#,###.00` has no placeholder for the integer zero, so an amount below a
   * dollar renders without one — `$.50`, not `$0.50` — and the currency symbol
   * sits inside the format, which puts a minus sign ahead of it.
   */
  export function formatTotal(amount: number): string {
    const rounded = roundCurrency(amount)
    const sign = rounded < 0 ? '-' : ''
    const digits = _DECIMAL.format(Math.abs(rounded)).replace(/^0(?=\.)/, '')
    return `${sign}$${digits}`
  }

  /** Rounds an amount to cents. */
  export function roundCurrency(amount: number): number {
    return Math.round(amount * 100) / 100
  }

  const _DECIMAL = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  })
}
